import { titleCase, type BrandArtDto, type ClientProcessDto, type ClientViewDto, type ProjectFunnelDto, type ProjectHomeDto, type ProjectCompletenessDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient, useApi, type ApiError } from '../../api.ts';
import { ProjectHeader } from '../../components/Art.tsx';
import { headerSite } from '../../components/siteHeader.ts';
import { CostBar } from '../../components/CostBar.tsx';
import { StepPanel } from '../../components/process/StepPanel.tsx';
import { TreeSheet } from '../../components/process/TreeSheet.tsx';
import { useJobProcess, useProcessModel } from '../../components/process/useProcess.ts';
import { WorkingOnSection } from '../../components/process/WorkingOn.tsx';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { WayRound } from '../../components/WayRound.tsx';
import { homeSection, WAY_ROUND, type WayRoundPath } from '../../components/homeWords.ts';
import { Introduction, INTRODUCTION_ON, IntroductionMark } from '../../components/Introduction.tsx';
import { BudgetWaitingRow, DeviationWaitingRow, PatternsWaitingRow, WaitingOnYou } from './Waiting.tsx';
import { YoursRow } from './YoursRow.tsx';
import { PriceFunnel } from '../../components/PriceFunnel.tsx';
import { funnelSentence } from '../../components/funnelSentence.ts';
import { Button, CompletenessBar, ErrorLine, LoadError, Loading, Page, SectionTitle, type HowTo } from '../../components/ui.tsx';
import { endedDay, endedWords } from './ended.ts';
import { ProjectMenu } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { TestTag } from '../../components/TestProject.tsx';
import { ProjectStatusTag } from '../../components/ProjectStatus.tsx';
import { statusTag } from '../../components/statusTag.ts';
import { addedWords } from './reviewStep.ts';

/** /p/:id opens on the single thing that matters for the role (spec §13). */
export function ProjectEntry() {
  const projectId = useProjectId();
  const { data: project, error, isLoading } = useProject(projectId);
  if (error) return <LoadError error={error} />;
  if (isLoading || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  if (project.viewerRole === 'client') return <ClientHome projectId={projectId} />;
  return <ContractorHome projectId={projectId} />;
}

/**
 * Contractor project home (style guide v2 §12): code, title, place, sentence,
 * bar, then the work. The place's banner lies full-bleed behind the title,
 * exactly as on the client's home (Jason, 2026-10-03), and what is waiting on
 * NSB sits beside the bar where the client's "Waiting on you" does.
 */
function ContractorHome({ projectId }: { projectId: string }) {
  const { data, error } = useQuery<ProjectHomeDto, ApiError>({
    queryKey: ['project', projectId, 'home'],
    queryFn: () => api<ProjectHomeDto>(`/projects/${projectId}/home`),
    // The banner is being painted: look again until it is there.
    refetchInterval: (q) => (q.state.data?.bannerPainting ? 8000 : false),
  });
  useFeedbackRecord(projectId, data ? { type: 'project', id: projectId, label: data.project.name } : null);
  // A lead made from an email is marked on Projects until NSB opens it here (spec "Intake" §4).
  const fromEmail = Boolean(data?.project.fromEmail);
  useEffect(() => {
    if (!fromEmail) return;
    api(`/projects/${projectId}/seen`, { body: {} })
      .then(() => queryClient.invalidateQueries({ queryKey: ['pipeline'] }))
      .catch(() => undefined);
  }, [fromEmail, projectId]);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const p = data.project;
  const needs = data.needs.length && !p.ended ? data.needs : [];
  // Its details to add, once it is a project (issue #3: started from its title and goal).
  const adding = Boolean(p.convertedAt) && !p.ended && data.completeness.done < data.completeness.of;
  const aside = needs.length > 0 || adding;
  // Its states only, when there are any: the project's code is not another name to read over the title.
  const states = p.isSample || statusTag(p.status) !== null;
  const site = headerSite(p);
  return (
    <Page wide>
      <ProjectHeader
        banner={data.banner}
        title={p.name}
        place={site.words}
        placeMap={site.map}
        kicker={
          states ? (
            <>
              {p.isSample ? <TestTag /> : null}
              <ProjectStatusTag status={p.status} />
            </>
          ) : null
        }
      >
        {!data.banner && data.bannerPainting ? <p className="label mt-3">Painting this month’s banner · a few minutes</p> : null}
      </ProjectHeader>
      {/* Under the name and the place, where the banner has faded to paper, so it can be read (Jason, 2026-10-03). */}
      <ProjectMenu projectId={projectId} pathname={`/p/${projectId}`} inline />
      <div className={aside ? 'grid gap-x-16 gap-y-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,23rem)]' : ''}>
        <div className="min-w-0">
          {p.ended ? (
            <p className="mt-4 max-w-measure">
              Cancelled {endedWords(p.ended)}.{p.ended.reason ? ` ${p.ended.reason}` : ''}
            </p>
          ) : null}
          <p className="mb-5 mt-8 max-w-column font-display text-[1.3125rem] leading-snug sm:text-[1.5rem]">{data.costBar.sentence}</p>
          <CostBar data={data.costBar} budgetHref={`/p/${projectId}/budget`} />
          {!data.costBar.ceiling && !data.costBar.pendingBudget && !data.costBar.internal && !p.ended ? (
            <p className="meta mt-2">
              No client budget yet.{' '}
              <Link to={`/p/${projectId}/budget`} className="link">
                Propose the one they gave you
              </Link>
            </p>
          ) : null}
          {!p.convertedAt ? <StartProject projectId={projectId} /> : null}
        </div>
        {aside ? (
          <div className="space-y-12 self-start lg:mt-8">
            {needs.length ? (
              // Beside the sentence and the bar, each line the way to the work itself (Jason, 2026-10-03: "take me right to the comment").
              <section>
                <div className="border-b border-ink/40 pb-2">
                  <h2 className="text-[1.375rem]">Waiting on you</h2>
                </div>
                <ul className="divide-y divide-rule border-b border-rule">
                  {needs.map((n) => (
                    <li key={n.text}>
                      {n.to ? (
                        <Link to={`/p/${projectId}/${n.to}`} className="group flex items-baseline justify-between gap-4 py-3">
                          <span className="text-ink-2 group-hover:text-ink group-hover:underline">{n.text}</span>
                          <span className="link shrink-0 text-sm text-ink-2">Open</span>
                        </Link>
                      ) : (
                        <p className="py-3 text-ink-2">{n.text}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {adding ? <ToAdd projectId={projectId} completeness={data.completeness} /> : null}
          </div>
        ) : null}
      </div>
      {p.convertedAt ? <FunnelSection projectId={projectId} staff /> : null}
      {p.convertedAt && !p.ended ? <ProcessSection projectId={projectId} staff /> : null}
    </Page>
  );
}

/**
 * A project's details, added as they are needed (issue #3), under a bar of
 * how many are, as the tree builder shows a node: each not yet added in grey,
 * the way to where it is added.
 */
function ToAdd({ projectId, completeness }: { projectId: string; completeness: ProjectCompletenessDto }) {
  const missing = completeness.details.filter((d) => !d.done);
  return (
    <section>
      <div className="border-b border-ink/40 pb-2">
        <h2 className="text-[1.375rem]">To add</h2>
      </div>
      <p className="mt-3 flex items-center gap-3">
        <CompletenessBar done={completeness.done} of={completeness.of} label={`${completeness.done} of ${completeness.of} added`} className="w-32" />
        <span className="text-[0.9375rem]">
          <span className="num">{completeness.done}</span> of <span className="num">{completeness.of}</span> added
        </span>
      </p>
      <ul className="mt-3 divide-y divide-rule border-y border-rule">
        {missing.map((d) => (
          <li key={d.key}>
            <Link to={`/p/${projectId}/${d.to}`} className="group flex items-baseline justify-between gap-4 py-3">
              <span className="text-ink-2 group-hover:text-ink group-hover:underline">{d.words}</span>
              <span className="link shrink-0 text-sm text-ink-2">Add</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A lead becomes a project in one go, as a new one is started; its home then asks for the rest. */
function StartProject({ projectId }: { projectId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/projects/${projectId}/start`, { body: {} });
      await Promise.all([queryClient.invalidateQueries({ queryKey: ['project', projectId] }), queryClient.invalidateQueries({ queryKey: ['pipeline'] })]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-8">
      <Button onClick={() => void start()} busy={busy}>
        Start the project
      </Button>
      <ErrorLine error={error} />
    </div>
  );
}

/**
 * The price funnel under a project's home: the contractor's with the reasons, and the client's, which NSB's preview
 * asks for as the client's (B14: `?as=client`, under its own key, never NSB's own steps or notes).
 */
function FunnelSection({ projectId, staff }: { projectId: string; staff: boolean }) {
  const { data } = useQuery<ProjectFunnelDto, ApiError>({
    queryKey: staff ? ['project', projectId, 'funnel'] : ['project', projectId, 'funnel', 'client'],
    queryFn: () => api<ProjectFunnelDto>(`/projects/${projectId}/funnel${staff ? '' : '?as=client'}`),
    // The seasons are being painted: look again now and then, they arrive a year at a time.
    refetchInterval: (q) => (q.state.data?.seasonsPainting ? 10000 : false),
  });
  // Nothing to draw and no step that will deliver an estimate: no section. With a delivering step the sentence says
  // where the first estimate comes, before anything is drawn (design study 17).
  if (!data || (!data.band.length && !data.gates.some((g) => g.on || g.deliversEstimate))) return null;
  return (
    <section className="mt-14">
      <div className="mb-3 border-b border-rule pb-2">
        <h2 className="text-[1.375rem] sm:text-[1.5rem]">{homeSection('funnel')}</h2>
      </div>
      {/* The sentence over the funnel: its facts, in the reader's words, never a figure nobody set (design study 17). */}
      <p className="mb-5 max-w-2xl text-ink-2">{funnelSentence(data, { client: !staff })}</p>
      <PriceFunnel funnel={data} staff={staff} />
    </section>
  );
}

/**
 * The client portal (spec §2, style guide v2 §12): it should feel like opening
 * a well-made project letter. Title, the place, the sentence, the bar, their
 * way round, what is waiting on them; then the funnel, where the job is, and
 * the design board.
 */
export function ClientHome({ projectId, preview = false }: { projectId: string; preview?: boolean }) {
  const { data, error } = useApi<ClientViewDto>(['project', projectId, 'client-view'], `/projects/${projectId}/client-view`);
  const { data: brand } = useApi<BrandArtDto>(['art', 'brand'], '/art/brand');
  useFeedbackRecord(projectId, data ? { type: 'project', id: projectId, label: data.projectName } : null);
  // The Introduction (design study 11): open by itself the first time this person sees the project, by the ? after.
  // Only a client's own reading is recorded; the preview records nothing, and a refusal (viewing as, in production) is
  // nothing to tell them about.
  const [intro, setIntro] = useState<'unasked' | 'open' | 'closed'>('unasked');
  const introOpen = INTRODUCTION_ON && (intro === 'open' || (intro === 'unasked' && Boolean(data) && !data!.introduction.opened && !preview));
  const record = (step: 'opened' | 'finished') => {
    if (preview) return;
    api<ClientViewDto['introduction']>(`/projects/${projectId}/introduction`, { method: 'POST', body: { step } })
      .then((introduction) => queryClient.setQueryData<ClientViewDto>(['project', projectId, 'client-view'], (old) => (old ? { ...old, introduction } : old)))
      .catch(() => undefined);
  };
  const openIntro = () => {
    setIntro('open');
    record('opened');
  };
  const closeIntro = (finished: boolean) => {
    setIntro('closed');
    if (finished) record('finished');
    else if (data && !data.introduction.opened) record('opened');
  };
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  // A step come late, or a gate marked done early, for them to confirm (design study 21).
  const deviations = data.deviationsWaiting ?? [];
  // The place's watercolor lies full-bleed behind the title (design board, client home); the title sits low in it.
  return (
    <Page wide>
      {introOpen ? <Introduction example={data.example} onClose={closeIntro} /> : null}
      {preview ? (
        <div className="mb-3">
          <Link to={`/p/${projectId}`} className="label -ml-2 inline-block rounded-[2px] bg-paper px-2 py-1 hover:!text-ink">
            ← Back · this is what the client sees
          </Link>
        </div>
      ) : null}
      <div className="grid gap-x-16 gap-y-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,23rem)]">
        <div className="min-w-0">
          <ProjectHeader banner={data.banner} title={data.projectName} place={data.place} beside={INTRODUCTION_ON ? <IntroductionMark finished={data.introduction.finished} onOpen={openIntro} /> : undefined} />
          {data.ended ? <p className="mt-4">This project ended on {endedDay(data.ended)}.</p> : null}
          {data.costBar ? (
            <>
              <p className="mb-7 mt-8 max-w-column font-display text-[1.5625rem] leading-snug sm:text-[1.875rem]">{data.costBar.sentence}</p>
              <CostBar data={data.costBar} size="client" reader="client" budgetHref={preview ? null : `/p/${projectId}/budget`} />
            </>
          ) : null}
          {/* Their way round the project, just under the price (Jason, 2026-10-03): always all of it, so nothing is hidden until it fills. */}
          <WayRound
            className="mt-6"
            to={(path) => `/p/${projectId}/${path}`}
            items={WAY_ROUND.filter((w) => (w.path !== 'decisions' || data.shows.process) && (w.path !== 'patterns' || data.sections.includes('patterns'))) // Their own steps of the process, while NSB shows it to them (Still to decide, A); their patterns once the step is reached.
              .map((w) => {
                const counts: Record<WayRoundPath, string | number | null> = {
                  budget: null,
                  timeline: null,
                  files: data.sharedFiles || null,
                  board: data.boardItems || null,
                  patterns: data.patternsWaiting ? 'to lock' : null,
                  reviews: data.reviewsWaiting.length ? `${data.reviewsWaiting.length} to review` : null,
                  decisions: data.now?.yours.length ? `${data.now.yours.length} waiting` : null,
                };
                return { path: w.path, label: w.path === 'budget' && !data.budgetSet ? w.unset : w.label, count: counts[w.path] };
              })}
          />

        </div>

        {data.ended ? null : (
        // Over the watercolor on wide screens, so it sits on its own sheet of paper.
        <WaitingOnYou className={data.banner ? 'lg:mt-36 lg:border lg:border-rule lg:bg-paper/95 lg:p-6' : ''}>
          {data.budgetWaiting ? (
            <ul className="divide-y divide-rule border-b border-rule">
              <BudgetWaitingRow split={data.budgetWaiting.split} by={data.budgetWaiting.by} to={`/p/${projectId}/budget`} />
            </ul>
          ) : null}
          {data.patternsWaiting ? (
            // NSB's request to lock the pattern language (spec "Pattern Mapping" §4.5).
            <ul className="divide-y divide-rule border-b border-rule">
              <PatternsWaitingRow by={data.patternsWaiting.by} {...(preview ? {} : { to: `/p/${projectId}/patterns` })} />
            </ul>
          ) : null}
          {data.reviewsWaiting.length ? (
            <ul className="divide-y divide-rule border-b border-rule">
              {data.reviewsWaiting.map((r) => (
                <li key={r.setId}>
                  <Link to={`/p/${projectId}/reviews?set=${r.setId}`} className="group flex items-baseline justify-between gap-4 py-3.5">
                    <span>
                      <span className="block group-hover:underline">Review the {titleCase(r.title)}</span>
                      <span className="meta">
                        {r.phase}, iteration {r.iteration}
                      </span>
                      {/* Asked again by sheets NSB added (design study 29): what is new in it. */}
                      {addedWords(r.added) ? <span className="meta block">{addedWords(r.added)}</span> : null}
                    </span>
                    <span className="link shrink-0 text-sm text-ink-2">Open</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {data.now?.yours.length ? (
            // Their own steps the work has reached (Jason, 2026-10-06): a decision to make, or something of theirs to do.
            <ul className="divide-y divide-rule border-b border-rule">
              {data.now.yours.map((y) => (
                <YoursRow key={y.id} projectId={projectId} step={y} preview={preview} />
              ))}
            </ul>
          ) : null}
          {deviations.length ? (
            // What NSB proposed for them to confirm (design study 21): a step that comes late, a gate marked done early.
            <ul className="divide-y divide-rule border-b border-rule">
              {deviations.map((d) => (
                <DeviationWaitingRow key={d.id} projectId={projectId} deviation={d} preview={preview} />
              ))}
            </ul>
          ) : null}
          {data.reviewsWaiting.length === 0 && !data.budgetWaiting && !data.now?.yours.length && !deviations.length && !data.patternsWaiting ? (
            // The reward state (spec §13), as the concept's phone view draws it (docs/design/studies/01, panel 4):
            // a still lake under a pine bough fading into the paper, the width of a phone's screen, and the words below.
            <div className="pb-12 pt-2 text-center">
              {brand?.emptyReward ? (
                <img
                  src={`${brand.emptyReward.url}?w=960`}
                  srcSet={brand.emptyReward.widths.map((w) => `${brand.emptyReward!.url}?w=${w} ${w}w`).join(', ')}
                  sizes="(min-width: 640px) 24rem, 100vw"
                  alt=""
                  className="mb-4 block w-full max-sm:-mx-(--gutter) max-sm:w-[calc(100%+2*var(--gutter))] max-sm:max-w-none sm:mx-auto sm:max-w-[24rem]"
                  width={brand.emptyReward.width}
                  height={brand.emptyReward.height}
                />
              ) : null}
              <p className="font-display text-[1.75rem] leading-tight sm:text-[2rem]">Nothing is waiting on you.</p>
              <p className="mt-2 font-display text-[1.25rem] text-ink-2">Enjoy the quiet.</p>
            </div>
          ) : null}
        </WaitingOnYou>
        )}
      </div>
      {data.shows.funnel ? <FunnelSection projectId={projectId} staff={false} /> : null}
      {data.shows.process && !data.ended ? <ProcessSection projectId={projectId} /> : null}
      {/* The design board (process-flow spec): here, below the way, a standing space that never competes with what is waiting. */}
      <section className="mt-14 max-w-3xl">
        <div className="mb-3 border-b border-rule pb-2">
          <h2 className="text-[1.375rem] sm:text-[1.5rem]">{homeSection('board')}</h2>
        </div>
        <p className="max-w-2xl text-ink-2">
          {data.boardItems
            ? `${data.boardItems} ${data.boardItems === 1 ? 'thing' : 'things'} collected so far. Add what you are drawn to, whenever it strikes you: when the finishes come due, you choose from here.`
            : 'A place to collect what you are drawn to: pictures, Pinterest pins, links, a few words. When the finishes come due, you choose from here.'}{' '}
          <Link to={`/p/${projectId}/board`} className="link text-ink">
            Open the board
          </Link>
        </p>
      </section>
    </Page>
  );
}

/**
 * The project's process under the price and the funnel, on the client's home
 * and on NSB's alike (Jason, 2026-10-05: "the client view should see the money
 * bar the funnel and then below that the process tree for their project";
 * then, for NSB's home: "money bar, funnel, then the process tree as applied
 * to this project with in the client display mode, with what is being activly
 * worked on"), as the client sees it, every box with its picture and its
 * details. Read down the page, never panned or zoomed (TreeSheet; design
 * study 06, revision 2): done, under way (ringed), or not started yet and
 * greyed. A step pressed opens in place with what it is and its tasks. Over it,
 * Working on (design study 31): what is under way and the mark it works toward,
 * a tile pressed there opening its step in the tree; the tree is the Project
 * process tree, its key in its help.
 */
function ProcessSection({ projectId, staff = false }: { projectId: string; staff?: boolean }) {
  const { data } = useApi<ClientProcessDto>(['project', projectId, 'client-process'], `/projects/${projectId}/client-process`);
  const [open, setOpen] = useState<string | null>(null);
  if (!data?.shown || !data.steps.length) return null;
  return (
    <>
      <WorkingOnSection dto={data} onOpen={setOpen} />
      <section className="mt-14">
        <SectionTitle how={treeHow(staff)}>{homeSection('tree')}</SectionTitle>
        <TreeSheet
          dto={data}
          projectId={projectId}
          label="The steps of the project"
          staff={staff}
          open={open}
          onOpen={setOpen}
          panel={staff ? (stepId, close) => <StepWork projectId={projectId} stepId={stepId} onClose={close} /> : undefined}
        />
      </section>
    </>
  );
}

/** What the tree is and how it is read (design study 31): the key that stood over it is here. */
const treeHow = (staff: boolean): HowTo => ({
  line: 'Every step of this project, in the order the work goes.',
  intent: 'Every step this project has, phase by phase, top to bottom in the order the work goes: a line means the step under it waits for the one above.',
  steps: [
    `Done is moss, under way is ringed in cedar, waiting on ${staff ? 'the client' : 'you'} is ringed in bengara, and the rest is grey until it starts.`,
    staff ? 'Press a step to work on it.' : 'Press a step to see what it is and what’s in it.',
  ],
});

/**
 * A step opened on NSB's home: its own panel, where it is worked (its tasks, an inspection passed, a gate marked done,
 * its estimate, its dates and people), as the Process page had it (Jason, 2026-10-07: "overview has the whole jobs
 * process and phases, so I don't know why it would be also listed"). What it has is set up in Settings, Process.
 */
function StepWork({ projectId, stepId, onClose }: { projectId: string; stepId: string; onClose: () => void }) {
  const { data } = useJobProcess(projectId);
  const model = useProcessModel(data);
  return <div className="p-5">{model ? <StepPanel key={stepId} projectId={projectId} model={model} stepId={stepId} setup={false} onClose={onClose} /> : <Loading />}</div>;
}


export function ClientPreview() {
  const projectId = useProjectId();
  return <ClientHome projectId={projectId} preview />;
}

/**
 * One of the client's own steps the work has reached, on their Waiting on you: what it is in their words, and a way to
 * mark it decided or done, with a word on it if they like (POST /process/steps/:id/decide, on the record).
 */
